import 'tslib';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import { TransactionLoaderKey } from '../enums';
import {
  PaginatedTransactions,
  Transaction,
  TransactionFileUploadRequest,
  TransactionFilters,
  TransactionListParams,
  TransactionMetrics,
  TransactionStep,
  TransactionUI,
} from '../models';
import { ApiService, LoadingService, SnackbarService } from '../services';
import { cleanHTTPParams } from '@core/utils/helpers';

const ENDPOINT = 'v1/transactions';

@Injectable({ providedIn: 'root' })
export class TransactionsService {
  constructor(
    private api: ApiService,
    private http: HttpClient,
    private loader: LoadingService,
    private snack: SnackbarService,
  ) {}

  metrics(): Observable<TransactionMetrics> {
    return this.api
      .get<TransactionMetrics>(`${ENDPOINT}/metrics`)
      .pipe(first(), this.loader.set(TransactionLoaderKey.GET_TRANSACTION_METRICS), this.snack.operator());
  }

  recent(): Observable<TransactionUI[]> {
    return this.api
      .get<TransactionUI[]>(`${ENDPOINT}/recent`)
      .pipe(first(), this.loader.set(TransactionLoaderKey.GET_RECENT_TRANSACTION), this.snack.operator());
  }

  filters(): Observable<TransactionFilters> {
    return this.api.get<TransactionFilters>(`${ENDPOINT}/filters`).pipe(first());
  }

  list(params: TransactionListParams): Observable<PaginatedTransactions> {
    const httpParams = new HttpParams({ fromObject: cleanHTTPParams(params) });

    return this.api
      .get<PaginatedTransactions>(`${ENDPOINT}`, { params: httpParams })
      .pipe(first(), this.loader.set(TransactionLoaderKey.LIST_TRANSACTION), this.snack.operator());
  }

  get(id: string): Observable<Transaction> {
    return this.api
      .get<Transaction>(`${ENDPOINT}/${id}`)
      .pipe(first(), this.loader.set(TransactionLoaderKey.GET_TRANSACTION), this.snack.operator());
  }

  getSteps(id: string): Observable<TransactionStep[]> {
    return this.api.get<TransactionStep[]>(`${ENDPOINT}/${id}/steps`);
  }

  preview(id: string): Observable<string> {
    return this.api
      .get<string>(`${ENDPOINT}/${id}/preview`, { responseType: 'text' as 'json' })
      .pipe(first(), this.loader.set(TransactionLoaderKey.GET_TRANSACTION_PREVIEW), this.snack.operator());
  }

  upload(payload: TransactionFileUploadRequest, file: File): Observable<string> {
    const requestData: FormData = this.prepareFormData(payload, file);
    return this.api
      .post<string, FormData>(`${ENDPOINT}/upload`, requestData)
      .pipe(first(), this.loader.set(TransactionLoaderKey.UPLOAD_TRANSACTION), this.snack.operator());
  }

  private prepareFormData(payload: TransactionFileUploadRequest, file: File): FormData {
    const formData: FormData = new FormData();
    formData.set('file', file);
    if (payload.receiverId) formData.set('receiverId', payload.receiverId);
    if (payload.messageType) formData.set('messageType', payload.messageType);
    return formData;
  }
}
